import React, { useEffect } from 'react';
import CommonGlobalRulesTable from 'Components/GlobalRulesTable';
import useGetRuleColumns, { Args } from 'Hooks/useGetRuleColumns';
import useRuleView from 'Hooks/useRuleView';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import { useSelector, useDispatch } from 'react-redux';
import { setGroupView } from 'Store';

import { getUser, getGroupView, shouldShowAdvancedRules } from 'Selectors';
import { GLOBAL_RULE_TABLE_VIEW } from 'Common/enums';
import { getRulesFormatForView } from 'Utils';

const GlobalRulesTable = ({ rulesData, data }: { rulesData: Rule[]; data: GlobalRulesData }) => {
    const { data: landlordInfo } = useGetLandlordInfo();
    const viewGroupOption = useSelector(getGroupView);
    const { viewOption, setViewOption } = useRuleView();
    const { tenantKey } = useSelector(getUser);
    const dispatch = useDispatch();
    useEffect(() => {
        if (viewGroupOption) {
            setViewOption(viewGroupOption);
            dispatch(setGroupView(''));
        }
    }, [viewGroupOption, setViewOption, dispatch]);
    const isGlobalRuleSelectOption = viewOption === GLOBAL_RULE_TABLE_VIEW;
    const globalColumnsToDisplay: Args['columns'] = [
        'modifiedFlag',
        'virtualRuleGroup',
        'ruleId',
        'inputDescription',
        'conclusion',
        'ruleType',
        'lastModified',
        'status'
    ];
    const executionColumnsToDisplay: Args['columns'] = [
        'modifiedFlag',
        'executionOrder',
        'ruleId',
        'inputDescription',
        'ruleGroup',
        'conclusion',
        'ruleType',
        'status'
    ];
    if (landlordInfo?.isLandlord) {
        globalColumnsToDisplay.push('allowOverrides');
        globalColumnsToDisplay.push('hiddenFromTenant');
    }
    const columnsToDisplay = isGlobalRuleSelectOption ? globalColumnsToDisplay : executionColumnsToDisplay;

    /** Hide columns if Feature Flags are turned off */
    let columns = columnsToDisplay;
    if (!shouldShowAdvancedRules()) {
        columns = columnsToDisplay.filter((columnName) => columnName !== 'ruleType');
    }

    const columnNames = useGetRuleColumns({
        columns
    });

    const groupByColumn = isGlobalRuleSelectOption ? ['virtualRuleGroup'] : ['executionOrder'];

    // Default or Templated Rules should not be seen in Execution Order view.
    const formatedRulesData = getRulesFormatForView(rulesData, isGlobalRuleSelectOption);

    return (
        <CommonGlobalRulesTable
            tableName={`${tenantKey}-sandbox-global-rules`}
            rulesData={formatedRulesData}
            columnNames={columnNames}
            mode='sandbox'
            groupByColumns={groupByColumn}
            emptyGroupsData={data?.zeroRuleGroups}
            rulesGroupData={data?.groups}
            sandboxRules={rulesData}
            selectViewOption={viewOption}
            setSelectViewOption={setViewOption}
            options={{
                isRevisionHistoryVisible: true
            }}
        />
    );
};

export default React.memo(GlobalRulesTable);
